import type { GameState } from './game'

export type ApiUser = {
  id: string
  provider: 'casdoor'
  email: string | null
  displayName: string | null
}

export type AuthSession = {
  user: ApiUser | null
  csrfToken: string | null
  auth: { provider: 'casdoor'; configured: boolean }
}

export type PhoneVerificationResponse = {
  delivery: 'signup' | 'login'
  message: string
  retryAfter: number
}

export type SaveDocument = {
  revision: number
  contentVersion: string
  gameSchemaVersion: 2
  state: GameState['coreState']
  checksum: string
  updatedAt: string
  deviceId: string
}

export type Entitlement = {
  productId: 'sun_vip' | 'sun_svip'
  status: 'active'
  source: 'demo_claim' | 'legacy_claim' | 'wechat_h5' | 'alipay_wap' | 'apple_storekit'
  grantedAt: string
}

export type AppleStoreKitProductId = 'com.deepcubetech.sunharvest.vip' | 'com.deepcubetech.sunharvest.svip'
export type AppleStoreKitVerification = Entitlement & { transactionId: string }

export type PaymentProvider = 'wechat' | 'alipay'
export type PaymentProduct = 'sun_vip' | 'sun_svip'
export type PaymentOrder = {
  orderId: string
  provider: PaymentProvider
  productId: PaymentProduct
  amountFen: number
  currency: 'CNY'
  status: 'pending' | 'paid' | 'closed' | 'failed'
  checkoutUrl: string | null
  expiresAt: string
  paidAt: string | null
}

export type SaveWritePayload = {
  mutationId: string
  expectedRevision: number
  deviceId: string
  contentVersion: string
  gameSchemaVersion: 2
  state: GameState['coreState']
}

export type SaveImportPayload = SaveWritePayload & { legacySource: 'sun_v5_v1' }

export type ResetPayload = {
  mutationId: string
  expectedRevision: number
  deviceId: string
  scope: 'run' | 'all'
}

type ErrorBody = { code: string; message: string; details?: unknown }
type ApiResponse<T> = { data: T; requestId: string }
type ApiErrorResponse = { error: ErrorBody; requestId: string }
type FetchLike = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>
type RequestContext = { sessionRequestVersion?: number; timeoutMs?: number; signal?: AbortSignal }

const PAYMENT_REQUEST_TIMEOUT_MS = 8_000

export class ApiClientError extends Error {
  readonly status: number | null
  readonly code: string
  readonly details: unknown
  readonly requestId: string | null

  constructor(message: string, options: { status?: number | null; code?: string; details?: unknown; requestId?: string | null } = {}) {
    super(message)
    this.name = 'ApiClientError'
    this.status = options.status ?? null
    this.code = options.code ?? 'API_ERROR'
    this.details = options.details
    this.requestId = options.requestId ?? null
  }
}

export class UnauthorizedError extends ApiClientError {
  constructor(message = '登录已失效，请重新登录', requestId: string | null = null) {
    super(message, { status: 401, code: 'UNAUTHORIZED', requestId })
    this.name = 'UnauthorizedError'
  }
}

export class SaveConflictError extends ApiClientError {
  readonly currentRevision: number | null

  constructor(message: string, details: unknown, requestId: string | null) {
    super(message, { status: 409, code: 'SAVE_CONFLICT', details, requestId })
    this.name = 'SaveConflictError'
    this.currentRevision = isRecord(details) && typeof details.currentRevision === 'number' ? details.currentRevision : null
  }
}

export class NetworkOfflineError extends ApiClientError {
  constructor(message = '无法连接云存档服务；请检查网络后重试') {
    super(message, { code: 'NETWORK_OFFLINE' })
    this.name = 'NetworkOfflineError'
  }
}

export type ApiClientOptions = {
  baseUrl?: string
  fetch?: FetchLike
  deviceStorage?: Storage | null
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function sortCanonicalValue(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(sortCanonicalValue)
  if (isRecord(value)) {
    return Object.keys(value).sort().reduce<Record<string, unknown>>((sorted, key) => {
      sorted[key] = sortCanonicalValue(value[key])
      return sorted
    }, {})
  }
  return value
}

// Keep this byte-for-byte compatible with the API's canonicalJson/checksumJson:
// object keys are sorted recursively, while array order and scalar values stay
// unchanged. This is used for equality only; writes still send the original
// state object.
export function canonicalJson(value: unknown): string {
  return JSON.stringify(sortCanonicalValue(value))
}

function uuid(): string {
  const webCrypto = globalThis.crypto
  if (webCrypto?.randomUUID) return webCrypto.randomUUID()
  if (!webCrypto?.getRandomValues) throw new Error('当前环境不支持安全随机数，无法生成设备标识')
  const bytes = new Uint8Array(16)
  webCrypto.getRandomValues(bytes)
  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80
  const hex = [...bytes].map(byte => byte.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

export function createMutationId(): string { return uuid() }

const DEVICE_ID_KEY = 'sun-ge-cloud-device-id-v1'

export function createDeviceId(): string { return uuid() }

export function getDeviceId(storage?: Storage | null): string {
  try {
    const resolvedStorage = storage === undefined
      ? (typeof window === 'undefined' ? null : window.localStorage)
      : storage
    const existing = resolvedStorage?.getItem(DEVICE_ID_KEY)
    if (existing) return existing
    const created = createDeviceId()
    resolvedStorage?.setItem(DEVICE_ID_KEY, created)
    return created
  } catch {
    // Private browsing/storage-disabled environments still get a usable per-call id.
    return createDeviceId()
  }
}

export function createSavePayload(
  state: GameState,
  contentVersion: string,
  options: { deviceId?: string; mutationId?: string; expectedRevision?: number } = {}
): SaveWritePayload {
  if (!contentVersion) throw new TypeError('contentVersion is required for a cloud save')
  return {
    mutationId: options.mutationId ?? createMutationId(),
    expectedRevision: options.expectedRevision ?? state.saveRevision,
    deviceId: options.deviceId ?? getDeviceId(),
    contentVersion,
    gameSchemaVersion: 2,
    state: state.coreState
  }
}

function joinUrl(baseUrl: string, path: string): string {
  return `${baseUrl.replace(/\/$/, '')}${path}`
}

function requestIdFrom(response: Response, body: unknown): string | null {
  if (isRecord(body) && typeof body.requestId === 'string') return body.requestId
  return response.headers.get('x-request-id')
}

async function readBody(response: Response): Promise<unknown> {
  if (response.status === 204) return null
  try { return await response.json() } catch { return null }
}

export class ApiClient {
  private readonly baseUrl: string
  private readonly fetcher: FetchLike
  private csrfToken: string | null = null
  private sessionRequestVersion = 0

  constructor(options: ApiClientOptions = {}) {
    this.baseUrl = (options.baseUrl ?? (import.meta.env.VITE_API_BASE || '/api/v1')).replace(/\/$/, '')
    // Browsers expose fetch as a method on Window. Passing the bare method
    // loses its receiver in real browsers and can throw Illegal invocation.
    this.fetcher = options.fetch ?? globalThis.fetch.bind(globalThis)
  }

  private async request<T>(path: string, init: RequestInit = {}, context: RequestContext = {}): Promise<T> {
    const headers = new Headers(init.headers)
    headers.set('accept', 'application/json')
    if (init.body !== undefined) headers.set('content-type', 'application/json')
    const timeoutController = context.timeoutMs ? new AbortController() : null
    let timeoutId: ReturnType<typeof globalThis.setTimeout> | undefined
    let removeExternalAbortListener: (() => void) | undefined
    const requestInit: RequestInit = { ...init, headers, credentials: 'include' }
    if (timeoutController) {
      requestInit.signal = timeoutController.signal
      if (context.signal) {
        const abort = () => timeoutController.abort()
        if (context.signal.aborted) abort()
        else {
          context.signal.addEventListener('abort', abort, { once: true })
          removeExternalAbortListener = () => context.signal?.removeEventListener('abort', abort)
        }
      }
      timeoutId = globalThis.setTimeout(() => timeoutController.abort(), context.timeoutMs)
    } else if (context.signal) requestInit.signal = context.signal
    try {
      const response = await this.fetcher(joinUrl(this.baseUrl, path), requestInit)
      const body = await readBody(response)
      const requestId = requestIdFrom(response, body)
      if (!response.ok) {
        const error = isRecord(body) && isRecord(body.error) ? body.error as ErrorBody : null
        const code = error?.code ?? `HTTP_${response.status}`
        const message = error?.message ?? `云存档请求失败（HTTP ${response.status}）`
        if (response.status === 401) {
          // A session GET carries no CSRF header, so token equality cannot
          // identify its generation. Only the current session request may
          // clear the token; older GET responses must be inert. For other
          // requests, clear only the token that request actually used.
          const sessionIsCurrent = context.sessionRequestVersion === undefined
            || context.sessionRequestVersion === this.sessionRequestVersion
          const sentToken = headers.get('x-csrf-token')
          const requestOwnsCurrentToken = Boolean(sentToken && sentToken === this.csrfToken)
          if ((context.sessionRequestVersion !== undefined && sessionIsCurrent) || requestOwnsCurrentToken) this.csrfToken = null
          throw new UnauthorizedError(message, requestId)
        }
        if (code === 'SAVE_CONFLICT') throw new SaveConflictError(message, error?.details, requestId)
        throw new ApiClientError(message, { status: response.status, code, details: error?.details, requestId })
      }
      if (!isRecord(body) || !('data' in body) || typeof body.requestId !== 'string') {
        throw new ApiClientError('云存档返回了无法识别的响应', { status: response.status, code: 'INVALID_RESPONSE', requestId })
      }
      return (body as ApiResponse<T>).data
    } catch (error) {
      if (error instanceof ApiClientError) throw error
      if (context.signal?.aborted) {
        throw new ApiClientError('支付请求已取消。', { code: 'REQUEST_CANCELLED' })
      }
      if (timeoutController?.signal.aborted) {
        throw new ApiClientError('支付请求超时，请检查网络后重试；重试会复用同一订单，不会重复下单。', { code: 'REQUEST_TIMEOUT' })
      }
      throw new NetworkOfflineError(error instanceof Error ? error.message : undefined)
    } finally {
      if (timeoutId !== undefined) globalThis.clearTimeout(timeoutId)
      removeExternalAbortListener?.()
    }
  }

  async getSession(): Promise<AuthSession> {
    const version = ++this.sessionRequestVersion
    const session = await this.request<AuthSession>('/auth/session', { cache: 'no-store' }, { sessionRequestVersion: version })
    if (version === this.sessionRequestVersion) this.csrfToken = session.csrfToken
    return session
  }

  async sendPhoneCode(phone: string, countryCode = 'CN'): Promise<PhoneVerificationResponse> {
    return this.request<PhoneVerificationResponse>('/auth/phone/send-code', {
      method: 'POST',
      body: JSON.stringify({ phone, countryCode })
    })
  }

  async verifyPhoneCode(phone: string, code: string, countryCode = 'CN'): Promise<AuthSession> {
    return this.request<AuthSession>('/auth/phone/verify', {
      method: 'POST',
      body: JSON.stringify({ phone, code, countryCode })
    })
  }

  async logout(): Promise<{ loggedOut: true }> {
    const version = ++this.sessionRequestVersion
    try {
      if (!this.csrfToken) throw new ApiClientError('登录会话不完整，请重新登录', { code: 'CSRF_REQUIRED' })
      return await this.request<{ loggedOut: true }>('/auth/logout', {
        method: 'POST',
        headers: this.csrfHeaders()
      })
    } finally {
      if (version === this.sessionRequestVersion) this.csrfToken = null
    }
  }

  async getPlayerProfile(): Promise<{ nickname: string | null; avatarUrl: string | null }> {
    return this.request('/me/profile', { cache: 'no-store' })
  }

  async setPlayerProfile(nickname: string): Promise<{ nickname: string; avatarUrl: string | null }> {
    return this.request('/me/profile', { method: 'PUT', headers: this.csrfHeaders(), body: JSON.stringify({ nickname }) })
  }

  async setPlayerAvatar(image: string): Promise<{ nickname: string; avatarUrl: string }> {
    return this.request('/me/profile/avatar', { method: 'PUT', headers: this.csrfHeaders(), body: JSON.stringify({ image }) })
  }

  async getSave(): Promise<SaveDocument | null> {
    return this.request<SaveDocument | null>('/me/save', { cache: 'no-store' })
  }

  async putSave(payload: SaveWritePayload): Promise<SaveDocument> {
    return this.request<SaveDocument>('/me/save', {
      method: 'PUT', headers: this.csrfHeaders(), body: JSON.stringify(payload)
    })
  }

  async importSave(payload: SaveImportPayload): Promise<SaveDocument> {
    return this.request<SaveDocument>('/me/save/import', {
      method: 'POST', headers: this.csrfHeaders(), body: JSON.stringify(payload)
    })
  }

  async resetSave(payload: ResetPayload): Promise<SaveDocument> {
    return this.request<SaveDocument>('/me/save/reset', {
      method: 'POST', headers: this.csrfHeaders(), body: JSON.stringify(payload)
    })
  }

  async getEntitlements(): Promise<Entitlement[]> {
    const result = await this.request<{ items: Entitlement[] }>('/me/entitlements', { cache: 'no-store' })
    return result.items
  }

  async verifyApplePurchase(payload: { mutationId: string; productId: AppleStoreKitProductId; transactionId: string }): Promise<AppleStoreKitVerification> {
    return this.request<AppleStoreKitVerification>('/me/entitlements/apple/verify', {
      method: 'POST',
      headers: this.csrfHeaders(),
      body: JSON.stringify(payload)
    })
  }

  async claimVip(tier: 'vip' | 'svip' = 'vip'): Promise<Entitlement> {
    return this.request<Entitlement>(`/me/entitlements/sun-${tier}/claim`, {
      method: 'POST',
      headers: this.csrfHeaders(),
      body: JSON.stringify({ mutationId: createMutationId(), source: 'demo_claim' })
    })
  }

  async createPayment(tier: 'vip' | 'svip', provider: PaymentProvider, mutationId = createMutationId(), signal?: AbortSignal): Promise<PaymentOrder> {
    return this.request<PaymentOrder>('/me/payments', {
      method: 'POST',
      headers: this.csrfHeaders(),
      body: JSON.stringify({ mutationId, provider, productId: `sun_${tier}` })
    }, { timeoutMs: PAYMENT_REQUEST_TIMEOUT_MS, signal })
  }

  async getPayment(orderId: string, signal?: AbortSignal): Promise<PaymentOrder> {
    return this.request<PaymentOrder>(`/me/payments/${encodeURIComponent(orderId)}`, { cache: 'no-store' }, { timeoutMs: PAYMENT_REQUEST_TIMEOUT_MS, signal })
  }

  async refreshPayment(orderId: string, signal?: AbortSignal): Promise<PaymentOrder> {
    return this.request<PaymentOrder>(`/me/payments/${encodeURIComponent(orderId)}/refresh`, {
      method: 'POST',
      headers: this.csrfHeaders(),
      body: JSON.stringify({})
    }, { timeoutMs: PAYMENT_REQUEST_TIMEOUT_MS, signal })
  }

  private csrfHeaders(): HeadersInit {
    if (!this.csrfToken) throw new ApiClientError('登录会话不完整，请重新登录', { code: 'CSRF_REQUIRED' })
    return { 'x-csrf-token': this.csrfToken }
  }
}

export const apiClient = new ApiClient()
export const getSession = () => apiClient.getSession()
export const logout = () => apiClient.logout()
export const getSave = () => apiClient.getSave()
export const putSave = (payload: SaveWritePayload) => apiClient.putSave(payload)
export const importSave = (payload: SaveImportPayload) => apiClient.importSave(payload)
export const resetSave = (payload: ResetPayload) => apiClient.resetSave(payload)
export const getEntitlements = () => apiClient.getEntitlements()
export const claimVip = () => apiClient.claimVip()
